import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DataTable from "../../lib/components/DataTable";
import { getColumns } from "./constant.js";
import Pagination from "../../lib/components/Pagination/Pagination.jsx";
import useFleetSearchReducer from "./hooks/useFleetSearchHook/useFleetSearchHook.js";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider.jsx";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel.jsx";
import MultiSelect from "../../lib/components/MultiSelect/MultiSelect.jsx";
import {
  Settings,
  SlidersHorizontal,
  Search,
  Download,
  X,
  Bookmark,
  BookmarkCheck,
  Rows3,
  Rows4,
  AlignJustify,
  Filter,
} from "lucide-react";
import { Button, Checkbox, Tooltip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import useIsDarkTheme from "../../lib/hooks/useIsDarkTheme";
import { matchesColumnFilter } from "../../lib/utils/tableUtils";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import useHierarchy from "../../lib/hooks/useHierarchy";
import { isEnphaseUser } from "../../lib/utils/utility";
import "./FleetMetrics.scss";

export default function FleetMetrics() {
  const { serialNumber, siteId, setState } = useGlobalFilters();
  const location = useLocation();
  const [allTableData, setAllTableData] = useState([]); // All data from API
  const { state: fleetSearchState, refetch: refetchFleet } = useFleetSearchReducer();
  const filterDebounceRef = useRef(null);
  const enphaseUser = isEnphaseUser();
  const { stations: hierarchyStations, isLoading: isHierarchyLoading } = useHierarchy({ enabled: !enphaseUser });
  const lastProcessedDataRef = useRef(fleetSearchState?.data);
  const drillDownAppliedRef = useRef(false);
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [page, setPage] = useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const navigate = useNavigate();
  const isDark = useIsDarkTheme();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter and sort state (lifted from DataTable)
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showFilters, setShowFilters] = useState(false);
  const [showAppliedFilters, setShowAppliedFilters] = useState(true);
  const [quickSearch, setQuickSearch] = useState("");

  // Row selection state (#3)
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Table density (#14)
  const [density, setDensity] = useState("comfortable"); // compact | comfortable | spacious

  // Saved views (#10)
  const [savedViews, setSavedViews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fleet-saved-views") || "[]");
    } catch {
      return [];
    }
  });
  const [showSavedViews, setShowSavedViews] = useState(false);

  // Slide-out preview row (#12)
  const [, setPreviewRow] = useState(null);

  // Visible columns: manage in this page (DataTable is dumb and just respects it)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Show a small, important subset by default
    const defaultVisibleKeys = [
      "serialNumber",
      "siteId",
      "stationName",
      "sku",
      "displayName",
      "country",
      "fwVersion",
      "isConnected",
    ];
    return getColumns().reduce((acc, col) => {
      acc[col.key] = defaultVisibleKeys.includes(col.key);
      return acc;
    }, {});
  });

  // Ensure new columns get an entry in visibleColumns
  useEffect(() => {
    setVisibleColumns(prev => {
      const next = { ...prev };
      const defaultVisibleKeys = [
        "serialNumber",
        "siteId",
        "stationName",
        "sku",
        "displayName",
        "country",
        "fwVersion",
        "isConnected",
      ];
      getColumns().forEach(col => {
        if (!(col.key in next)) {
          next[col.key] = defaultVisibleKeys.includes(col.key);
        }
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isProfileMenuOpen) return;

    const handleOutsideClick = event => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, [isProfileMenuOpen]);

  const handleColumnVisibilityToggle = key => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Merge hierarchy station names into charger data (both from React Query cache)
  useEffect(() => {
    const incoming = Array.isArray(fleetSearchState?.data) ? fleetSearchState.data : [];
    lastProcessedDataRef.current = fleetSearchState?.data;

    if (incoming.length > 0 && hierarchyStations.length > 0) {
      const siteToStationMap = new Map();
      hierarchyStations.forEach(station => {
        if (station.sites && Array.isArray(station.sites)) {
          station.sites.forEach(site => {
            if (site.siteId) {
              siteToStationMap.set(site.siteId, {
                stationName: station.stationName || station.name || "Unknown Station",
                stationId: station.id || station.stationId,
              });
            }
          });
        }
      });

      const mergedData = incoming.map(charger => {
        const stationInfo = siteToStationMap.get(charger.siteId);
        return {
          ...charger,
          stationName: stationInfo?.stationName || "Unknown Station",
          stationId: stationInfo?.stationId || null,
        };
      });

      setAllTableData(mergedData);
    } else {
      setAllTableData(incoming);
    }

    // Reset pagination and selection when new data comes in (but keep filters intact)
    setPage(0);
    setSelectedRows(new Set());
  }, [fleetSearchState?.data, hierarchyStations]);

  // ── Drill-down: apply filters from Dashboard navigation state ──
  useEffect(() => {
    const drillDownFilters = location.state?.drillDownFilters;
    if (drillDownFilters && !drillDownAppliedRef.current) {
      drillDownAppliedRef.current = true;
      // Set column filters in the UI for display
      const uiFilters = {};
      Object.entries(drillDownFilters).forEach(([key, value]) => {
        uiFilters[key] = String(value);
      });
      setFilters(uiFilters);
      setShowFilters(true);
      // Trigger API call with the drill-down filters
      refetchFleet(drillDownFilters);
      // Clear the location state so back/forward navigation doesn't re-apply
      window.history.replaceState({}, "");
    }
  }, [location.state, refetchFleet]);

  // Cmd+K shortcut (#11)
  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const columns = getColumns(
    (serialNumber, siteId, timezone) => {
      setState({ serialNumber, siteId, siteTimezone: timezone || "UTC", isSerialNumberConfirmed: true });
      navigate(`/chargers/${serialNumber}`);
    },
    stationId => {
      if (stationId) {
        navigate(`/stations/manage/${stationId}`);
      }
    }
  ).filter(col => !(enphaseUser && col.key === "stationName"));

  // Fields the API supports for exact-match filtering via the `is` payload
  const API_FILTERABLE_KEYS = useMemo(
    () =>
      new Set([
        "serialNumber",
        "siteId",
        "sku",
        "protocol",
        "protocolVersion",
        "vendorName",
        "partNum",
        "modelName",
        "displayName",
        "country",
        "fwVersion",
        "commissioningStatus",
        "mode",
        "phaseCount",
        "maxCurrent",
        "dlbSource",
        "timezone",
        "isConnected",
        "isRetired",
        "isFaulted",
        "dlbEnabled",
        "isOffPeakEligible",
        "isDefaultOffPeakEligible",
        "isDeviceReady",
        "isOffGrid",
        "hoControl",
      ]),
    []
  );

  // Boolean column keys — their filter text maps to true/false for the API
  const BOOL_KEYS = useMemo(
    () =>
      new Set([
        "isConnected",
        "isRetired",
        "isFaulted",
        "dlbEnabled",
        "isOffPeakEligible",
        "isDefaultOffPeakEligible",
        "isDeviceReady",
        "isOffGrid",
        "hoControl",
      ]),
    []
  );

  // Build API params from column filters (only non-empty, API-supported keys)
  const buildApiParams = useCallback(
    filterObj => {
      const params = {};
      Object.entries(filterObj).forEach(([key, value]) => {
        const trimmed = String(value).trim();
        if (!trimmed || !API_FILTERABLE_KEYS.has(key)) return;

        if (BOOL_KEYS.has(key)) {
          const lower = trimmed.toLowerCase();
          if (["true", "yes", "1", "connected"].includes(lower)) {
            params[key] = true;
          } else if (["false", "no", "0", "disconnected"].includes(lower)) {
            params[key] = false;
          }
        } else if (!isNaN(Number(trimmed)) && key === "maxCurrent") {
          params[key] = Number(trimmed);
        } else {
          params[key] = trimmed;
        }
      });
      return params;
    },
    [API_FILTERABLE_KEYS, BOOL_KEYS]
  );

  // Step 1: Filter ALL data (column filters + quick search)
  const filteredData = useMemo(() => {
    if (!allTableData || allTableData.length === 0) return [];

    const q = quickSearch.trim().toLowerCase();

    return allTableData.filter(row => {
      // Column-level filters — skip columns already handled by the API
      const passesColumnFilters = columns.every(col => {
        // If this column was sent to the API, skip client-side filtering for it
        if (API_FILTERABLE_KEYS.has(col.key) && filters[col.key]?.trim()) {
          const apiVal = buildApiParams({ [col.key]: filters[col.key] });
          if (Object.keys(apiVal).length > 0) return true; // API handled it
        }
        return matchesColumnFilter(col, row, filters);
      });
      if (!passesColumnFilters) return false;

      // Quick search across all visible string values
      if (q) {
        return Object.values(row).some(val => {
          if (val == null) return false;
          return String(val).toLowerCase().includes(q);
        });
      }
      return true;
    });
  }, [allTableData, columns, filters, quickSearch, API_FILTERABLE_KEYS, buildApiParams]);

  // Step 2: Sort filtered data
  const sortedAndFilteredData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    if (!sortConfig.key) return filteredData;

    let result = [...filteredData];

    result.sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Convert to comparable values
      let compareA = aValue;
      let compareB = bValue;

      // If both are numbers, compare as numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        compareA = aValue;
        compareB = bValue;
      }
      // If both are booleans, compare as booleans
      else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        compareA = aValue ? 1 : 0;
        compareB = bValue ? 1 : 0;
      }
      // If both are strings, try date parsing first, then string comparison
      else if (typeof aValue === "string" && typeof bValue === "string") {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        // If both are valid dates, compare as dates
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          compareA = dateA.getTime();
          compareB = dateB.getTime();
        } else {
          // Otherwise, compare as strings (case-insensitive)
          compareA = aValue.toLowerCase();
          compareB = bValue.toLowerCase();
        }
      }
      // Mixed types - convert to string for comparison
      else {
        compareA = String(aValue).toLowerCase();
        compareB = String(bValue).toLowerCase();
      }

      // Compare values
      if (compareA < compareB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (compareA > compareB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [filteredData, sortConfig]);

  // Step 3: Paginate sorted and filtered data
  const totalItems = sortedAndFilteredData?.length || 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 1;
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = sortedAndFilteredData.slice(startIndex, endIndex);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, []);

  // Row selection helpers (#3)
  const toggleRowSelection = useCallback(serialNum => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(serialNum)) next.delete(serialNum);
      else next.add(serialNum);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map(r => r.serialNumber)));
    }
  }, [selectedRows, paginatedData]);

  const isAllSelected = paginatedData.length > 0 && selectedRows.size === paginatedData.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < paginatedData.length;

  // CSV export (#2)
  const exportCSV = useCallback(
    (dataToExport = null) => {
      const rows = dataToExport || sortedAndFilteredData;
      if (rows.length === 0) return;
      const exportCols = columns.filter(c => visibleColumns[c.key] !== false);
      const header = exportCols.map(c => `"${c.label}"`).join(",");
      const csvRows = rows.map(row =>
        exportCols
          .map(c => {
            const val = row?.[c.key];
            if (val == null) return "";
            return `"${String(val).replace(/"/g, '""')}"`;
          })
          .join(",")
      );
      const csv = [header, ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fleet-search-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [sortedAndFilteredData, columns, visibleColumns]
  );

  const exportSelected = useCallback(() => {
    const rows = sortedAndFilteredData.filter(r => selectedRows.has(r.serialNumber));
    exportCSV(rows);
  }, [sortedAndFilteredData, selectedRows, exportCSV]);

  // Active filters for chips bar (#4)
  const activeFilters = useMemo(() => {
    const entries = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value && String(value).trim()) {
        const col = columns.find(c => c.key === key);
        entries.push({ key, label: col?.label || key, value: String(value) });
      }
    });
    if (quickSearch.trim()) {
      entries.push({ key: "__quickSearch", label: "Search", value: quickSearch.trim() });
    }
    return entries;
  }, [filters, quickSearch, columns]);

  const removeFilter = useCallback(
    key => {
      if (key === "__quickSearch") {
        setQuickSearch("");
      } else {
        const next = { ...filters };
        delete next[key];
        setFilters(next);
        // Trigger API refetch with updated filters
        if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
          refetchFleet(buildApiParams(next));
        }, 300);
      }
      setPage(0);
    },
    [filters, buildApiParams, refetchFleet]
  );

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setQuickSearch("");
    setPage(0);
    // Refetch with no filters (back to default)
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    refetchFleet({});
  }, [refetchFleet]);

  // Saved views (#10)
  const saveCurrentView = useCallback(() => {
    const name = prompt("Name this view:");
    if (!name) return;
    const view = { name, filters, sortConfig, visibleColumns, quickSearch, density };
    const next = [...savedViews, view];
    setSavedViews(next);
    localStorage.setItem("fleet-saved-views", JSON.stringify(next));
  }, [filters, sortConfig, visibleColumns, quickSearch, density, savedViews]);

  const loadView = useCallback(view => {
    if (view.filters) setFilters(view.filters);
    if (view.sortConfig) setSortConfig(view.sortConfig);
    if (view.visibleColumns) setVisibleColumns(view.visibleColumns);
    if (view.quickSearch != null) setQuickSearch(view.quickSearch);
    if (view.density) setDensity(view.density);
    setPage(0);
    setShowSavedViews(false);
  }, []);

  const deleteView = useCallback(
    idx => {
      const next = savedViews.filter((_, i) => i !== idx);
      setSavedViews(next);
      localStorage.setItem("fleet-saved-views", JSON.stringify(next));
    },
    [savedViews]
  );

  // Toolbar button sx helper
  const toolbarBtnSx = (isActive = false) => ({
    textTransform: "none",
    borderColor: isActive ? "#f47c20" : isDark ? "#374151" : "#d1d5db",
    borderRadius: "10px",
    color: isActive ? "#f47c20" : isDark ? "#d1d5db" : "#374151",
    backgroundColor: isActive ? (isDark ? "rgba(244, 124, 32, 0.1)" : "rgba(244, 124, 32, 0.06)") : "transparent",
    fontWeight: 600,
    fontSize: "0.78rem",
    px: 1.5,
    py: 0.6,
    minWidth: "auto",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#f47c20",
      backgroundColor: isDark ? "rgba(244, 124, 32, 0.08)" : "rgba(244, 124, 32, 0.04)",
      color: "#f47c20",
    },
  });

  // Density icon
  const densityIcon =
    density === "compact" ? (
      <Rows4 size={15} />
    ) : density === "spacious" ? (
      <AlignJustify size={15} />
    ) : (
      <Rows3 size={15} />
    );
  const nextDensity = density === "compact" ? "comfortable" : density === "comfortable" ? "spacious" : "compact";

  // Checkbox column prepended to columns for selection (#3)
  const checkboxColumn = {
    key: "__select",
    label: "",
    sortable: false,
    filterable: false,
    width: "48px",
    render: (_, row) => (
      <Checkbox
        size="small"
        checked={selectedRows.has(row.serialNumber)}
        onClick={e => e.stopPropagation()}
        onChange={() => toggleRowSelection(row.serialNumber)}
        sx={{ p: 0, color: isDark ? "#4b5563" : "#d1d5db", "&.Mui-checked": { color: "#f47c20" } }}
      />
    ),
  };

  // Prepend checkbox + ensure __select is always visible
  const columnsWithCheckbox = [checkboxColumn, ...columns];
  const visibleColumnsWithCheckbox = { __select: true, ...visibleColumns };

  const hasPendingMerge = fleetSearchState?.data !== lastProcessedDataRef.current;
  const isPageLoading = fleetSearchState?.isLoading || isHierarchyLoading || hasPendingMerge;

  return (
    <FeatureGate featureId={FEATURES.FLEET_SEARCH}>
      <div className="fleet-search">
        {/* Toolbar */}
        <motion.div
          className="fleet-search__toolbar"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="fleet-search__toolbar-left">
            <div className="fleet-search__search">
              <Search size={14} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Quick search across all columns..."
                value={quickSearch}
                onChange={e => {
                  setQuickSearch(e.target.value);
                  setPage(0);
                }}
              />
              <kbd className="fleet-search__kbd">{navigator.platform?.includes("Mac") ? "\u2318" : "Ctrl"}K</kbd>
            </div>
          </div>
          <div className="fleet-search__toolbar-right">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowFilters(prev => !prev)}
              startIcon={<SlidersHorizontal size={15} />}
              sx={toolbarBtnSx(showFilters)}
            >
              {showFilters ? "Hide Filters" : "Filters"}
            </Button>
            {activeFilters.length > 0 && (
              <Tooltip title={showAppliedFilters ? "Hide applied filters" : "Show applied filters"} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowAppliedFilters(prev => !prev)}
                  startIcon={<Filter size={15} />}
                  sx={toolbarBtnSx(showAppliedFilters)}
                >
                  Applied ({activeFilters.length})
                </Button>
              </Tooltip>
            )}
            <MultiSelect
              options={columns}
              selectedValues={visibleColumns}
              onSelectionChange={handleColumnVisibilityToggle}
              buttonLabel="Columns"
              searchPlaceholder="Search columns..."
              emptyMessage="No columns found"
              buttonIcon={<Settings size={15} />}
              getOptionLabel={option => option.label}
              getOptionId={option => option.key}
            />
            {/* Density toggle (#14) */}
            <Tooltip title={`Density: ${density}`} arrow>
              <Button size="small" variant="outlined" onClick={() => setDensity(nextDensity)} sx={toolbarBtnSx()}>
                {densityIcon}
              </Button>
            </Tooltip>
            {/* Export CSV (#2) */}
            <Tooltip title="Export CSV" arrow>
              <Button
                size="small"
                variant="outlined"
                onClick={() => exportCSV()}
                startIcon={<Download size={15} />}
                sx={toolbarBtnSx()}
                disabled={totalItems === 0}
              >
                Export
              </Button>
            </Tooltip>
            {/* Saved views (#10) */}
            <div className="fleet-search__saved-views-wrap" ref={profileMenuRef}>
              <Tooltip title="Saved Views" arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowSavedViews(prev => !prev)}
                  sx={toolbarBtnSx(showSavedViews)}
                >
                  {savedViews.length > 0 ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
                </Button>
              </Tooltip>
              <AnimatePresence>
                {showSavedViews && (
                  <motion.div
                    className="fleet-search__saved-views-dropdown"
                    initial={{ opacity: 0, y: -6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="fleet-search__saved-views-header">
                      <span>Saved Views</span>
                      <button className="fleet-search__saved-views-save" onClick={saveCurrentView}>
                        + Save Current
                      </button>
                    </div>
                    {savedViews.length === 0 ? (
                      <div className="fleet-search__saved-views-empty">No saved views yet</div>
                    ) : (
                      savedViews.map((view, idx) => (
                        <div key={idx} className="fleet-search__saved-views-item">
                          <button className="fleet-search__saved-views-name" onClick={() => loadView(view)}>
                            {view.name}
                          </button>
                          <button className="fleet-search__saved-views-delete" onClick={() => deleteView(idx)}>
                            <X size={12} />
                          </button>
                        </div>
                      ))
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Active Filter Chips (#4) */}
        <AnimatePresence>
          {showAppliedFilters && activeFilters.length > 0 && (
            <motion.div
              className="fleet-search__filter-chips"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              {activeFilters.map(f => (
                <span key={f.key} className="fleet-search__filter-chip">
                  <span className="fleet-search__filter-chip-label">{f.label}:</span> {f.value}
                  <button className="fleet-search__filter-chip-remove" onClick={() => removeFilter(f.key)}>
                    <X size={11} />
                  </button>
                </span>
              ))}
              <button className="fleet-search__filter-chip-clear" onClick={clearAllFilters}>
                Clear All
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bulk Actions Bar (#3) */}
        <AnimatePresence>
          {selectedRows.size > 0 && (
            <motion.div
              className="fleet-search__bulk-bar"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <span className="fleet-search__bulk-count">{selectedRows.size} selected</span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download size={14} />}
                onClick={exportSelected}
                sx={toolbarBtnSx()}
              >
                Export Selected
              </Button>
              <button className="fleet-search__bulk-clear" onClick={() => setSelectedRows(new Set())}>
                <X size={13} /> Clear
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Empty State (#9) — shown when no data loaded at all */}
        {!isPageLoading && allTableData.length === 0 && (
          <motion.div
            className="fleet-search__empty-state"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="fleet-search__empty-icon">
              <Search size={40} />
            </div>
            <h3 className="fleet-search__empty-title">No Chargers Found</h3>
            <p className="fleet-search__empty-text">
              Enter a Serial Number or Site ID in the sidebar to search your fleet. Your results will appear here with
              sorting, filtering, and export options.
            </p>
          </motion.div>
        )}

        {/* Table */}
        {(allTableData.length > 0 || isPageLoading) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            <DataTable
              columns={columnsWithCheckbox}
              data={paginatedData}
              visibleColumns={visibleColumnsWithCheckbox}
              filters={filters}
              onFiltersChange={setFilters}
              sortConfig={sortConfig}
              onSortConfigChange={setSortConfig}
              loading={isPageLoading}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(prev => !prev)}
              density={density}
              selectAllChecked={isAllSelected}
              selectAllIndeterminate={isSomeSelected}
              onSelectAll={toggleSelectAll}
            />
            <Pagination
              totalPages={totalPages}
              currentPage={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalItems={totalItems}
            />
          </motion.div>
        )}

        {/* Slide-out preview (#12) */}
        {openChargerDetailsSlideout && (
          <SlideOutPanel
            open={openChargerDetailsSlideout}
            onClose={() => {
              setOpenChargerDetailsSlideout(false);
              setPreviewRow(null);
            }}
            serialNumber={serialNumber}
            siteId={siteId}
          />
        )}
      </div>
    </FeatureGate>
  );
}
